import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Switch from "@mui/material/Switch";
import MaterialTable from "material-table";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { tableIcons } from "../../pages/new-ticket/AddAsset";
import { storeFiltersValue } from "../../redux/actions/TicketsManagement/GetTickets";
import { IState } from "../../redux/reducers/rootReducers";
import timeSpanToReadbleFormat from "../../util/timeSpanToReadbleFormat";
import { ConvertZ, handleTime } from "../helpers/helperFunctions";

// Constants
const COL_WIDTH: Record<string, number> = {
  assetInfo: 400,
  AssetEffort: 200,
  subject: 200,
  description: 300,
  nextActionDescription: 300,
  AssetDownTime: 200,
  openDateTime: 200,
};

const COLUMN_LABELS: Record<string, string> = {
  id: "Ticket ID",
  title: "Subject",
  description: "Description",
  ticketType: "Ticket Type",
  contactId: "Contact Known As",
  contactEmail: "Contact Email",
  contactJob: "Contact Job Title",
  Org: "Third Party Organisation",
  contactOrg: "Contact Organisation",
  secondaryContactId: "Secondary Contact",
  contactDiv: "Division",
  contactDep: "Department",
  contactLoc: "Location",
  openDateTime: "Open Date",
  dueDateTime: "Due Date",
  effort: "Total Effort",
  groupId: "Group",
  firstResponseDueDateTime: "First Action Date",
  nextActionDescription: "First Action Description",
  note: "Note",
  ownerId: "Owner",
  priority: "Priority",
  slaRemaining: "SLA Remaining",
  source: "Source",
  status: "Status",
  updatedDateTime: "Updated Date",
  assetInfo: "Asset Name",
  assetTag: "Asset Tag",
  // assetType: "Asset Type",
  // assestLifeSpan:"Asset LifeSpan",
  // assestCurrentValue:"Asset CurrentValue",
  // endOfLife:"End of Life",
  AssetDownTime: "Downtime",
  category: "Category",
  outComeCategory: "Outcome Category",
  AdditionalField: "Additional Field",
};

const PRINT_COLUMNS = [
  "id~Ticket ID",
  "title~Subject",
  "description~Description",
  "ticketType~Ticket Type",
  "contactId~Contact Known As",
  "contactEmail~Contact's Email",
  "contactJob~Contact's Job Title",
  "Org~Third party Organisation",
  "contactOrg~Contact Organisation",
  "secondaryContactId~Secondary Contact",
  "contactDiv~Division",
  "contactDep~Department",
  "contactLoc~Location",
  "openDateTime~Created Date",
  "dueDateTime~Due Date",
  "effort~Total effort",
  "firstResponseDueDateTime~First Action Date",
  "groupId~Group",
  "nextActionDescription~First Action Description",
  "note~Note",
  "ownerId~Owner",
  "priority~Priority",
  "slaRemaining~SLA Remaining",
  "source~Source",
  "status~Status",
  "updatedDateTime~Updated Date",
  "assetInfo~Assets name",
  "assetTag~Assets Tag",
  // "assetType~Assets Type",
  // "assestLifeSpan~Assests LifeSpan",
  // "endOfLife~end Of Life",
  // "assestCurrentValue~assest Current Value",
  "AssetDownTime~Downtime",
  "category~Category",
  "outComeCategory~Outcome Category",
  "AdditionalField~Additional Field",
];

const TRUNCATION_STYLE = {
  display: "block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  cursor: "pointer",
};

// Helper functions
const swapValues = <T,>(arr: T[], index1: number, index2: number): T[] => {
  const newArr = [...arr];
  [newArr[index1], newArr[index2]] = [newArr[index2], newArr[index1]];
  return newArr;
};

const formatTime = (timeString?: string): string => {
  if (!timeString) return "--";
  const [hours, minutes] = timeString.split(":");
  return `${hours}:${minutes}`;
};

// Extracted components
const DisplayKeyValue = ({ data }: { data: string }) => {
  const obj = useMemo(() => {
    return data.split(", ").reduce((acc: Record<string, string>, pair: string) => {
      const [key, ...rest] = pair.split(": ");
      const value = rest.join(": ").trim();
      if (value) {
        acc[key.trim()] = value;
      }
      return acc;
    }, {});
  }, [data]);

  return (
    <div>
      {Object.entries(obj).map(([key, value]) => (
        <div key={key}>
          {key.startsWith("CheckBox_Check") ? (
            <div>
              <strong>selected:</strong> {value}
            </div>
          ) : (
            <div>
              <strong>{key}:</strong> {value}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const AdditionalFieldRenderer = ({ rowData }: { rowData: { AdditionalField?: string } }) => {
  const fullText = rowData.AdditionalField || "";
  return (
    <Tooltip title={<DisplayKeyValue data={fullText} />} placement="top">
      <Typography sx={TRUNCATION_STYLE}>
        {fullText || "--"}
      </Typography>
    </Tooltip>
  );
};

const ReportExportCSV = (props: any) => {
  const { allTickets, setColumnVisibilityModel, chartRes, isViewOnly } = props;
  // console.log("columnVisibilityModel",allTickets);
  
  const dispatch = useDispatch();
  const [rows, setRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [dynamicColumns, setDynamicColumns] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const currAgent = useSelector((state: IState) => state?.agentReducer?.loggedInAgent);
  const loaderSf = useSelector((state: IState) => state?.LayoutTicketManagementReducer?.showLoader);
  const filtersVal = useSelector((state: IState) => state?.filtersTicketManagementReducerForCSV);
  const [isProcessing, setIsProcessing] = useState(true); // Add loading state
 
  // Initialize filters
  useEffect(() => {
    const isEmpty = Object.values(filtersVal).every(
      value => 
        (Array.isArray(value) && value.length === 0) || 
        (typeof value === 'string' && value === '') || 
        value === null || 
        value === 'unset'
    );
    
    if (isEmpty) {
      dispatch(storeFiltersValue([currAgent?.id], "TCKT_AGENTS_VALUE"));
    }
  }, [dispatch, currAgent?.id, filtersVal]);

  // Process data when tickets change
  const processTicketsData = useCallback(() => {
    if (!allTickets || allTickets.length === 0) {
      setIsProcessing(false); // Set loading to false if no tickets
      return;
    }
    const additionalFieldObject = columns.find((c: { field: string; }) => c.field === 'AdditionalField');
    const hiddenValue = additionalFieldObject ? additionalFieldObject.hidden : undefined;

    const modifiedArr: any[] = [];
    const newDynamicColumns: Record<string, any> = {};

    allTickets.forEach((ticket: any) => {
      if (!ticket?.ticketLogs) return;
      
      const logs = ticket.ticketLogs;
      const row: Record<string, any> = { id: ticket.id };


      // Process print columns
      PRINT_COLUMNS.forEach((col: string) => {
        const [key] = col.split("~");
        switch (key) {
          case "ticketType": row[key] = logs?.ticketType?.displayName || "--"; break;
          case "groupId": row[key] = logs?.group?.name || "--"; break;
          case "ownerId": 
            row[key] = logs?.agent ? `${logs.agent.firstName} ${logs.agent.lastName}` : "--";
            break;
          case "priority": row[key] = logs?.priority?.name || "--"; break;
          case "contactEmail": row[key] = logs?.contacts?.email || "--"; break;
          case "secondaryContactId": row[key] = logs?.secondaryContacts?.name || "--"; break;
          case "contactJob": row[key] = logs?.contacts?.jobTitle || "--"; break;
          case "contactId": row[key] = logs?.contacts?.name || "--"; break;
          case "contactLoc": row[key] = logs?.contacts?.officeLocation || "--"; break;
          case "Org": row[key] = logs?.contactOrganisation?.name || "--"; break;
          case "contactOrg": row[key] = logs?.contacts?.contactOrganisation?.name || "--"; break;
          case "contactDep": row[key] = logs?.contacts?.department?.name || "--"; break;
          case "contactDiv": row[key] = logs?.contacts?.division?.name || "--"; break;
          case "category": row[key] = logs?.category?.name || "--"; break;
          case "outComeCategory": row[key] = logs?.outComeCategory?.name || "--"; break;
          case "source": row[key] = ticket?.sourceCode || "--"; break;
          case "status": row[key] = logs?.ticketStatus?.displayName || "--"; break;
          case "updatedDateTime": row[key] = ConvertZ(logs?.createdDateTime) || "--"; break;
          case "effort": row[key] = formatTime(logs?.effort); break;
          case "AssetDownTime": row[key] = formatTime(logs?.downtime); break;
          case "openDateTime": row[key] = ConvertZ(logs?.openDateTime) || "--"; break;
          case "firstResponseDueDateTime": row[key] = ConvertZ(logs?.firstResponseDueDateTime) || "--"; break;
          case "dueDateTime": row[key] = ConvertZ(logs?.dueDateTime) || "--"; break;
          case "assestLifeSpan": row[key] = logs?.ticketAssetList?.[0].asset?.LifeSpan|| "--"; break;
          case "assestCurrentValue":row[key] =logs?.ticketAssetList?.[0]?.asset?.currentValue;break
          case "endOfLife":row[key] =logs?.ticketAssetList?.[0]?.asset?.endofLife ||" --";break
          //  assestLifeSpan:"Assests LifeSpan",
          default: row[key] = logs[key] || "--";
        }
      });

      // Common fields
      row["id"] = ticket.id;
      row.body = logs?.body || "--";
      row.description = logs?.description || "--";
      row.title = logs?.title || "--";
      row.nextActionDescription = logs?.nextActionDescription || "--";
      row.note = logs?.note || "--";
      row.slaRemaining = timeSpanToReadbleFormat(logs?.slaRemaining) || "--";

      // Additional fields
      if (logs?.ticketAdditionalField) {
        const additionalFields = Object.entries(logs.ticketAdditionalField)
          .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}`)
          .join(", ");
        
        row.AdditionalField = additionalFields;

        Object.entries(logs.ticketAdditionalField).forEach(([key, value]) => {
          row[key] = value;
          
          if (!newDynamicColumns[key]) {
            newDynamicColumns[key] = {
              title: key,
              field: key,
              hidden: hiddenValue,
              export: true,
              filtering: true,
              cellStyle: {
                width: "150px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "12px",
              },
            };
          }
        });
      } else {
        row.AdditionalField = "--";
      }

      // Asset info
      if (ticket?.ticketAssetList?.length > 0) {

        const assetNames = ticket.ticketAssetList.map((a: any) => a?.asset?.name).filter(Boolean);
        const assetTags = ticket.ticketAssetList.map((a: any) => a?.asset?.assetTag).filter(Boolean);
        const assetTypes = ticket.ticketAssetList
          .map((a: any) => a?.asset?.assetType?.displayName)
          .filter(Boolean);

        const endOfLife = ticket.ticketAssetList.map((a: any) => a?.asset?.endOfLife).filter(Boolean);
        const assestCurrentValue = ticket.ticketAssetList.map((a: any) => a?.asset?.assetCurrentValue).filter(Boolean);
        const assestLifeSpan = ticket.ticketAssetList.map((a: any) => a?.asset?.assetLifeSpan).filter(Boolean);

// console.log("ticket.ticketAssetList",ticket);

        row.assetInfo = assetNames.join(", ") || "--";
        row.assetType = assetTypes.join(", ") || "--";
        row.assetTag = assetTags.join(", ") || "--";
        row.endOfLife = endOfLife.join(", ") || "--";
        row.assestCurrentValue = assestCurrentValue.join(", ") || "--";
        row.assestLifeSpan = assestLifeSpan.join(", ") || "--";
      } else {
        row.assetInfo = "--";
      }

      modifiedArr.push(row);
    });

    setDynamicColumns(Object.values(newDynamicColumns));
    setRows(modifiedArr);
    setIsProcessing(false); 
  }, [allTickets]);

  // Setup data processing with debounce
  useEffect(() => {
    setIsProcessing(true); 
    if (!allTickets || allTickets.length === 0) {
      setIsProcessing(false);
      return;
    }
    handleTime.cancel();
    handleTime.setup(processTicketsData);
  }, [allTickets, processTicketsData]);

  // Initialize columns
  useEffect(() => {
    setIsProcessing(true)
    dispatch({ type: "SHOW_LOADER", payload: true });
    const getBaseColumns = () => {
      if (chartRes?.map) {
        return chartRes.filter((i: any) => COLUMN_LABELS[i.field]).map(({ field, hidden }: any) => {
          // const isAdditionalField = field === "AdditionalField";
          return {
            title: COLUMN_LABELS[field] || field,
            field,
            hidden,
            // customExportValue: isAdditionalField 
            //   ? (rowData: any) => rowData.AdditionalField 
            //   : undefined,
            // render: isAdditionalField
            //   ? (rowData: any) => <AdditionalFieldRenderer rowData={rowData} />
            //   : undefined,
            cellStyle: {
              width: COL_WIDTH[field] || "250px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "12px",
            },
          };
        });
      }

      return Object.entries(COLUMN_LABELS).map(([field, title]) => ({
        title,
        field,
        hidden: false,
        // ...(field === "AdditionalField" && {
        //   customExportValue: (rowData: any) => rowData.AdditionalField,
        //   render: (rowData: any) =>
        //      <AdditionalFieldRenderer rowData={rowData} />,
        // }),
        cellStyle: {
          width: COL_WIDTH[field] || "150px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: "12px",
        },
      }));
    };

    setColumns(getBaseColumns());
    dispatch({ type: "SHOW_LOADER", payload: false });
  }, [chartRes]);

  // Combine base and dynamic columns
  const combinedColumns = useMemo(() => {
    
    dispatch({ type: "SHOW_LOADER", payload: true });
    const baseWithoutAdditional = columns.filter(col => col.field !== "AdditionalField");
     dispatch({ type: "SHOW_LOADER", payload: false });
    return [...baseWithoutAdditional, ...dynamicColumns];
  }, [columns, dynamicColumns]);

  // Column handlers
  const handleToggleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleColumnDrag = useCallback((sourceIndex: number, destinationIndex: number) => {
    setColumns(prev => {
      const newColumns = swapValues(prev, sourceIndex, destinationIndex);
      setColumnVisibilityModel(newColumns);
      return newColumns.sort((a, b) => Number(a.hidden) - Number(b.hidden));
    });
  }, [setColumnVisibilityModel]);

  const toggleColumnVisibility = useCallback((
    field: string, 
    toggleAll?: boolean, 
    value?: boolean
  ) => {
    setColumns(prev => {
      const updated = prev.map(col => 
        toggleAll ? { ...col, hidden: value } :
        col.field === field ? { ...col, hidden: !col.hidden } : col
      );
      setColumnVisibilityModel(updated);
      return updated;
    });

    if (field === "AdditionalField" || toggleAll) {
      setDynamicColumns(prev =>  prev.map(col => ({ ...col, hidden: value ?? !col.hidden })))
    }
  }, [setColumnVisibilityModel]);

  // Memoized components
  const popoverContent = useMemo(() => (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Box className="text-center pt-1" sx={{display: 'flex', spaceBetween: '10px', gap: '10px', paddingLeft:'49px'}}>
        <Button variant="contained" size="small" onClick={() => toggleColumnVisibility("", true, false)}>
          Show All
        </Button>
        <Button variant="contained" size="small" onClick={() => toggleColumnVisibility("", true, true)}>
          Hide All
        </Button>
      </Box>
      <List>
        {columns.map(col => (
          <ListItem key={col.field} className="fields-list-toggler">
            <ListItemText primary={col.title} />
            <ListItemSecondaryAction>
              <Switch
                size="small"
                checked={!col.hidden}
                onChange={() => toggleColumnVisibility(col.field)}
              />
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Popover>
  ), [anchorEl, columns, toggleColumnVisibility]);
 
  return (
    <Box className="p-0">
      <div style={{ height: "100%", width: "100%" }}>
        {popoverContent}
        
        <Box className="csv-report-table">
          { !props?.isViewOnly && (
                <Box className="manage-btn">
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      onClick={handleToggleClick}
                      color="primary"
                      >
                    Manage Columns
                </Button>
            </Box>
          )}
          {(isProcessing) ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="200px"
            >
              <CircularProgress />
            </Box>
          ) :<MaterialTable
            columns={combinedColumns}
            title={""}
            data={rows}
            isLoading={loaderSf}
            options={{
              exportAllData: true,
              exportButton:  {
                  csv: true,
                  pdf: false,
                },
              padding: "dense",
              rowStyle: { height: "32px", fontSize: "14px" },
              headerStyle: { fontSize: "12px", padding: "14px" },
              pageSize: 100,
              maxBodyHeight: "calc(100vh - 150px)",
            }}
            icons={tableIcons}
            onColumnDragged={handleColumnDrag}
          />}
        </Box>
      </div>
    </Box>
  );
};

export default ReportExportCSV;